import React, {FC, memo, useEffect, useState} from "react";
import * as Yup from "yup";
// import { Form } from '@ant-design/compatible';
// import '@ant-design/compatible/assets/index.css';
import {Modal, Input, Form, Space, Button, message} from "antd";
import {Formik, FormikActions} from "formik";
import {orange, red} from "@ant-design/colors";
import {createInputHelpers} from "../../../helpers/createInputHelpers";
import LayoutForm from "../../../views/LayoutForm/LayoutForm";
import {isDefined} from "../../../helpers/typeScriptHelpers";
import i18n from "../../../services/i18n";
import {BookerPaymentMethodInput, GetBankDataQuery, useGetBankDataQuery} from "../../../generated/graphql";
import DraggableModal from "../../../views/DraggableModal/DraggableModal";
import {bicCountryMatchesIban, isValidBic, isValidIban} from "../../../../shared/lib/iban";

type ModalDataType = Pick<BookerPaymentMethodInput, "sepaAccountholder" | "iban" | "bic" | "bank">;

const {string, object} = Yup;

const SepaDebitValidationSchema = object().shape<ModalDataType>({
    sepaAccountholder: string().required(() => i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.accountHolder.help()),
    // Mod-97 + BIC-Format blockieren das Speichern (#899). Der Bank-Lookup unten ist nur Komfort
    // (BIC/Bankname vorbelegen) und konnte bis dahin eine falsche Prüfziffer nicht verhindern.
    iban: string()
        .required(i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.iban.help())
        .test("ibanMod97", () => i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.iban.invalid(), (value) => isValidIban(value)),
    bic: string()
        .required(i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.bic.help())
        .test("bicFormat", () => i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.bic.invalid(), (value) => isValidBic(value)),
    bank: string().required(i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.bank.help()),
});

type SepaDebitPaymentMethodModalProps = {
    initialValues?: ModalDataType;
    onSubmit?: (values: ModalDataType, formikActions: FormikActions<ModalDataType>) => void;
    onCancel?: () => void;
    open?: boolean;
    asModal?: boolean;
};

const defaultInitialValues: ModalDataType = {
    sepaAccountholder: "",
    iban: "",
    bic: "",
    bank: "",
};

const SepaDebitPaymentMethodModal: FC<SepaDebitPaymentMethodModalProps> = ({
    initialValues = defaultInitialValues,
    onSubmit = () => undefined,
    onCancel = () => undefined,
    open = false,
    asModal = true,
}) => {
    // fetching bank data from iban --------------------------------------------------------------------
    const [iban, setIban] = useState<string>();
    const [debouncedIban, setDebouncedIban] = useState<string>();
    const [bankData, setBankData] = useState<GetBankDataQuery["getBankData"]["bankData"]>(null);

    // Debounce: API-Call erst nach 600ms Pause und bei Mindestlaenge 15 Zeichen (kuerzeste IBAN)
    useEffect(() => {
        if (!iban || iban.replace(/\s/g, "").length < 15) {
            setDebouncedIban(undefined);
            return;
        }
        const timer = setTimeout(() => {
            setDebouncedIban(iban);
        }, 600);
        return () => clearTimeout(timer);
    }, [iban]);

    const {data: fetchedBankData} = useGetBankDataQuery({
        skip: !debouncedIban,
        variables: {iban: debouncedIban ?? ""},
        fetchPolicy: "network-only",
    });

    const fetchedBankDataIsValid = fetchedBankData?.getBankData?.valid === true;

    useEffect(() => {
        if (fetchedBankData?.getBankData?.valid === true) {
            setBankData(fetchedBankData?.getBankData?.bankData);
        } else {
            setBankData(null);
        }
    }, [fetchedBankData]);
    // -------------------------------------------------------------------------------------------------

    const NotValidatingMessage = () => {
        // For not german ibans we check the length of the iban
        // but nothing else
        const notValidingMessage =
            fetchedBankData?.getBankData?.messages[0] && fetchedBankData?.getBankData?.messages[0].includes("wird nicht validiert") ? fetchedBankData?.getBankData?.messages[0] : null;

        if (!notValidingMessage) return null;

        return <div style={{color: red[3]}}>{notValidingMessage}</div>;
    };

    const renderContent = (formikProps, createInputHelpers) => {
        const {values, touched, setFieldValue, handleBlur} = formikProps;
        const {handleTextInputChange, validateStatus, help} = createInputHelpers(formikProps);

        return (
            <LayoutForm columns={2}>
                <Form.Item
                    label={i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.accountHolder.label()}
                    validateStatus={validateStatus("sepaAccountholder")}
                    help={help("sepaAccountholder")}
                    style={{gridColumn: "span 2"}}
                    required
                >
                    <Input
                        data-testid="SepaDebitPaymentMethodModalAccountHolder"
                        onChange={handleTextInputChange("sepaAccountholder")}
                        onBlur={handleBlur("sepaAccountholder")}
                        value={isDefined(values.sepaAccountholder) ? values.sepaAccountholder : ""}
                    />
                </Form.Item>
                <Form.Item
                    label={i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.iban.label()}
                    help={
                        touched.iban && formikProps.errors.iban
                            ? formikProps.errors.iban
                            : fetchedBankDataIsValid || fetchedBankData?.getBankData === undefined
                            ? undefined
                            : isDefined(values.iban) === false
                            ? formikProps.touched.iban
                                ? i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.iban.help()
                                : undefined
                            : i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.iban.invalid()
                    }
                    validateStatus={touched.iban === undefined ? undefined : isDefined(values.iban) === false || fetchedBankDataIsValid === false || Boolean(formikProps.errors.iban) ? "error" : undefined}
                    style={{gridColumn: "span 2"}}
                    required
                >
                    <Input
                        data-testid="SepaDebitPaymentMethodModalIban"
                        // onChange={handleTextInputChange("iban")}
                        // onBlur={handleBlur("iban")}
                        onChange={(e) => {
                            // handleTextInputChange("iban");
                            formikProps.setFieldTouched("iban", true);
                            setFieldValue("iban", e.target.value);
                            setIban(e.target.value);
                            if (bankData?.bic) {
                                setFieldValue("bic", bankData.bic);
                            }
                            if (bankData?.name) {
                                setFieldValue("bank", bankData.name);
                            }
                            setBankData(fetchedBankData?.getBankData?.bankData);
                        }}
                        onBlur={(e) => {
                            // handleTextInputChange("iban");
                            setFieldValue("iban", e.target.value);
                            setIban(e.target.value);
                            if (bankData?.bic) {
                                setFieldValue("bic", bankData.bic);
                            }
                            if (bankData?.name) {
                                setFieldValue("bank", bankData.name);
                            }
                            formikProps.setFieldTouched("iban", true);
                        }}
                        // onBlur={handleBlur("iban")}
                        value={isDefined(values.iban) ? values.iban : ""}
                    />
                    <NotValidatingMessage />
                </Form.Item>
                <Form.Item
                    label={i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.bic.label()}
                    validateStatus={validateStatus("bic")}
                    help={help("bic")}
                    style={{gridColumn: "span 2"}}
                    required
                >
                    <Input data-testid="SepaDebitPaymentMethodModalBic" onChange={handleTextInputChange("bic")} onBlur={handleBlur("bic")} value={isDefined(values.bic) ? values.bic : ""} />
                    {isValidBic(values.bic) && !bicCountryMatchesIban(values.iban, values.bic) && (
                        <div data-testid="SepaDebitPaymentMethodModalBicCountryHint" style={{color: orange[6]}}>
                            {i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.bic.countryMismatch()}
                        </div>
                    )}
                </Form.Item>
                <Form.Item
                    label={i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.fields.bank.label()}
                    validateStatus={validateStatus("bank")}
                    help={help("bank")}
                    style={{gridColumn: "span 2"}}
                    required
                >
                    <Input data-testid="SepaDebitPaymentMethodModalBank" onChange={handleTextInputChange("bank")} onBlur={handleBlur("bank")} value={isDefined(values.bank) ? values.bank : ""} />
                </Form.Item>
            </LayoutForm>
        );
    };

    return (
        <Formik<ModalDataType>
            validationSchema={SepaDebitValidationSchema}
            initialValues={initialValues}
            enableReinitialize
            onSubmit={(values, formikActions) => {
                onSubmit(values, formikActions);
                formikActions.resetForm();
            }}
        >
            {(formikProps) => {
                const {submitForm, resetForm} = formikProps;

                if (asModal) {
                    return (
                        <DraggableModal
                            data-testid="SepaDebitPaymentMethodModal"
                            draggable={true}
                            boundsPadding={500}
                            wrapProps={{
                                "data-testid": "SepaDebitPaymentMethodModal",
                            }}
                            title={i18n.containers.customers.CustomersCreate.steps.bookerPaymentMethods.modals.sepaDebit.title()}
                            open={open}
                            onOk={submitForm}
                            onCancel={() => {
                                onCancel();
                                resetForm();
                            }}
                            getContainer="#modalmount"
                            zIndex={10000}
                            destroyOnClose
                        >
                            {renderContent(formikProps, createInputHelpers)}
                        </DraggableModal>
                    );
                }

                return (
                    <Space direction="vertical" style={{width: "100%"}}>
                        {renderContent(formikProps, createInputHelpers)}
                        <Button block type="primary" onClick={submitForm}>
                            {"Lastschriftdaten Speichern"}
                        </Button>
                    </Space>
                );
            }}
        </Formik>
    );
};

export default memo(SepaDebitPaymentMethodModal);
